import { useSupportLauncherContext } from "@hooks/context/useSupportLauncherContext";
import { Box, Stack, useTheme } from "@mui/material";
import Icon from "@ui/atoms/Icon/Icon";
import { ActionDialAction } from "@ui/atoms/ActionDial/ActionDial";
import { hide, onShow, show } from "@intercom/messenger-js-sdk";
import { useEffect, useMemo } from "react";
import { OutlineBadge } from "@ui/atoms/OutlineBadge/OutlineBadge";

// The assistant's `ai` mark. It used to cross-fade between lucide's Sparkle and
// Sparkles on an infinite loop so the glyph twinkled; the animation was dropped
// deliberately in the 2026-07-17 icon pass.
//
// `color` reaches the glyph through currentColor: Icon takes a `tone` from the
// theme's icon palette, and this caller needs an arbitrary one.
const SparklingIcon = ({ color }: { color: string }) => (
  <Box sx={{ color, display: "flex" }}>
    <Icon name="ai" />
  </Box>
);

/**
 * The id each support action is identified by. Consumers interleave these two
 * at their own positions rather than appending the list whole, so they have to
 * pick them out individually — by id, not by index. The list is built
 * conditionally (the assistant is gated), so an index is not a contract this
 * hook can keep.
 */
export const SUPPORT_ACTION_ID = {
  assistant: "global-assistant",
  contactSupport: "intercom"
} as const;

/**
 * Pick one support action out of the list by its id. Returns undefined when the
 * action is gated off — the assistant is, Contact Support never is.
 */
export const findSupportAction = (
  actions: ActionDialAction[],
  id: (typeof SUPPORT_ACTION_ID)[keyof typeof SUPPORT_ACTION_ID]
): ActionDialAction | undefined =>
  actions.find((action) => action.dataTestId === id);

// The support actions offered by the launcher: the Soprano Assistant (gated to
// where the assistant is enabled) and Contact Support (Intercom, always). This
// is the single definition for both actions; consumers fold the returned list
// into their own dial.
//
// Assistant ↔ Intercom are mutually exclusive: opening either closes the other.
export const useSupportActions = (): ActionDialAction[] => {
  const theme = useTheme();
  const { assistantEnabled, setAssistantOpen } = useSupportLauncherContext();

  // If the Intercom messenger is shown by any means (the action below, or a
  // proactive Intercom message), close the assistant drawer so only one support
  // surface is open at a time.
  useEffect(() => {
    if (typeof window.Intercom !== "function") return;
    onShow(() => setAssistantOpen(false));
  }, [setAssistantOpen]);

  return useMemo(() => {
    const openIntercom = () => {
      if (typeof window.Intercom !== "function") return;
      show();
    };

    // Opening the assistant closes the Intercom messenger (the inverse coupling
    // — onShow above — handles the other direction).
    const openAssistant = () => {
      if (typeof window.Intercom === "function") hide();
      setAssistantOpen(true);
    };

    const actions: ActionDialAction[] = [
      {
        dataTestId: SUPPORT_ACTION_ID.contactSupport,
        icon: <Icon name="support" />,
        onClick: openIntercom,
        title: "Contact Support"
      }
    ];

    if (assistantEnabled) {
      actions.push({
        dataTestId: SUPPORT_ACTION_ID.assistant,
        icon: <SparklingIcon color={theme.vars.palette.action.selected} />,
        onClick: openAssistant,
        title: (
          <Stack sx={{ alignItems: "center", flexDirection: "row", gap: 1 }}>
            <span>Catchment Agent</span>
            <OutlineBadge>Beta</OutlineBadge>
          </Stack>
        )
      });
    }

    return actions;
  }, [assistantEnabled, setAssistantOpen]);
};
